import { useEffect, useState } from 'react';
import { omit } from 'lodash';
import {
  createBulkDownloadFromSelection,
  getBulkDownloadFileList,
  getBulkDownloadTaskStatus,
  useApiMutation,
  useApiPolling,
  useApiQuery,
} from '@hum/api';
import { BulkDownloadStatus, BulkDownloadSelectedFileParams } from '@hum/types';

type Selection = Record<string, boolean>;

export const useBulkDownloadFromSelection = ({
  companyId,
}: {
  companyId: string;
}) => {
  const [fileFilter, setFileFilter] = useState('');
  const { data: fileList } = useApiQuery(getBulkDownloadFileList, {
    params: {
      companyId,
    },
  });
  const [selectAll, setSelectAll] = useState(true);
  const [selectedCustomerFiles, setSelectedCustomerFiles] = useState({});
  const [
    selectedSupportedFinancialDocuments,
    setSelectedSupportedFinancialDocuments,
  ] = useState({});

  const { mutate: downloadSelectedFiles } = useApiMutation(
    createBulkDownloadFromSelection,
    {
      onSuccess(data) {
        if (data?.taskId) {
          startPolling({ companyId, taskId: data.taskId });
        }
      },
    }
  );

  const {
    data: task,
    isPolling,
    startPolling,
    stopPolling,
    isError,
  } = useApiPolling(getBulkDownloadTaskStatus, {
    onPollSuccess(data) {
      if (!data) return;

      const isTerminalState = [
        BulkDownloadStatus.Failure,
        BulkDownloadStatus.Revoked,
        BulkDownloadStatus.Success,
      ].includes(data.state);

      if (isTerminalState || isError) {
        stopPolling();

        if (data.state === BulkDownloadStatus.Success && data.fileUrl) {
          window.location.assign(data.fileUrl);
        }
      }
    },
  });

  useEffect(() => {
    if (!fileList) {
      return;
    }

    setSelectedCustomerFiles(
      fileList.customerFiles.reduce(
        (files, file) => ({
          ...files,
          [file.id]: true,
        }),
        {}
      )
    );

    setSelectedSupportedFinancialDocuments(
      fileList.supportedFinancialDocuments.reduce(
        (files, file) => ({
          ...files,
          [`${file.connectorType}:${file.docType}:${file.connectorSource}`]: true,
        }),
        {}
      )
    );
  }, [fileList]);

  const handleDownloadSelectedClicked = () => {
    if (!fileList) {
      return;
    }

    const selected: BulkDownloadSelectedFileParams = {
      customerFiles: [],
      supportedFinancialDocuments: [],
    };

    for (const key in selectedCustomerFiles) {
      if (selectedCustomerFiles[key]) {
        selected.customerFiles.push(Number(key));
      }
    }

    for (const key in selectedSupportedFinancialDocuments) {
      if (selectedSupportedFinancialDocuments[key]) {
        const [connectorType, docType, connectorSource] = key.split(':');
        const financialDocument = fileList.supportedFinancialDocuments.find(
          (file) =>
            file.connectorType === connectorType &&
            file.docType === docType &&
            file.connectorSource === connectorSource
        );
        if (financialDocument) {
          selected.supportedFinancialDocuments.push(
            omit(financialDocument, 'lastSuccessPullAt')
          );
        }
      }
    }

    downloadSelectedFiles(selected);
  };

  const handleSelectAllChange = (value: boolean) => {
    setSelectAll(value);
    const newCustomerFiles = Object.keys(selectedCustomerFiles).reduce(
      (values, key) => ({
        ...values,
        [key]: value,
      }),
      {}
    );

    setSelectedCustomerFiles(newCustomerFiles);
    const newSupportedFinancialDocuments = Object.keys(
      selectedSupportedFinancialDocuments
    ).reduce(
      (values, key) => ({
        ...values,
        [key]: value,
      }),
      {}
    );
    setSelectedSupportedFinancialDocuments(newSupportedFinancialDocuments);
  };

  const handleCustomerFileChange = (key: number) => (value: boolean) => {
    const newCustomerFiles: Selection = {
      ...selectedCustomerFiles,
      [key]: value,
    };
    setSelectedCustomerFiles(newCustomerFiles);
    const values = Object.values(newCustomerFiles).concat(
      Object.values(selectedSupportedFinancialDocuments)
    );
    if (values.every((value: boolean) => value)) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  const handleSupportedFinancialDocumentsChange = (key: string) => (
    value: string
  ) => {
    const newSupportedFinancialDocuments: Selection = {
      ...selectedSupportedFinancialDocuments,
      [key]: value,
    };
    setSelectedSupportedFinancialDocuments(newSupportedFinancialDocuments);
    const values = Object.values(newSupportedFinancialDocuments).concat(
      Object.values(selectedCustomerFiles)
    );
    if (values.every((value: boolean) => value)) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  };

  return {
    fileFilter,
    fileList,
    handleCustomerFileChange,
    handleDownloadSelectedClicked,
    handleSelectAllChange,
    handleSupportedFinancialDocumentsChange,
    selectAll,
    selectedCustomerFiles,
    selectedSupportedFinancialDocuments,
    setFileFilter,
    task,
    isPolling,
    isError,
    stopDownload: stopPolling,
  };
};
